/**
 * React Query mutation hooks for sensitivity analysis.
 *
 * Sensitivity analysis is triggered on demand (slider changes) rather than
 * on page load, so we use useMutation rather than useQuery.
 */

import { useMutation } from '@tanstack/react-query';
import {
  calculateSensitivity,
  calculateCombinedSensitivity,
  calculateEnhancedSensitivity,
  calculateCombinedAdjustedSensitivity,
  calculateSpeedometerSensitivity,
} from '../../../lib/api/financialApi';
import type {
  SensitivityRequest,
  EnhancedSensitivityRequest,
  CombinedAdjustedSensitivityRequest,
  SpeedometerSensitivityRequest,
} from '../../../types/financial';

/**
 * Mutation hook for 2-variable sensitivity heatmap (revenue % × EBITDA margin %).
 *
 * Usage:
 *   const { mutate, data, isPending } = useSensitivityAnalysis();
 *   mutate({ company_name, year, revenue_range, ebitda_range });
 */
export const useSensitivityAnalysis = () =>
  useMutation({
    mutationFn: (request: SensitivityRequest) => calculateSensitivity(request),
  });

/**
 * Mutation hook for 5-parameter combined sensitivity scenario grid.
 *
 * Usage:
 *   const { mutate, data, isPending } = useCombinedSensitivity();
 *   mutate({ company_name, year, revenue_range, ebitda_range, wacc_range });
 */
export const useCombinedSensitivity = () =>
  useMutation({
    mutationFn: (request: SensitivityRequest) => calculateCombinedSensitivity(request),
  });

/**
 * Mutation hook for the enhanced 3-parameter sensitivity analysis.
 *
 * Sends { company, scenario_change_percent } to POST /sensitivity/enhanced/calculate.
 * Returns a tornado chart (sensitivity_indices) + full scenario grid for every year.
 *
 * Usage:
 *   const { mutate, data, isPending } = useEnhancedSensitivity();
 *   mutate({ company: "Nestle India", scenario_change_percent: [-0.06,-0.04,-0.02,0,0.02,0.04,0.06] });
 */
export const useEnhancedSensitivity = () =>
  useMutation({
    mutationFn: (request: EnhancedSensitivityRequest) => calculateEnhancedSensitivity(request),
  });

/**
 * Mutation hook for 5-parameter combined sensitivity with CAP adjustment.
 *
 * Varies Revenue, EBITDA, IC, WACC, and CAP. Auto-adjusts CAP=1 when CAP < WACC.
 * Default payload: { company, scenario_change_percent: [-0.06,-0.03,0,0.03,0.06] }
 *
 * Usage:
 *   const { mutate, data, isPending } = useCombinedAdjustedSensitivity();
 *   mutate({ company: "Allcargo Gati Ltd", scenario_change_percent: [-0.06,-0.03,0,0.03,0.06] });
 */
export const useCombinedAdjustedSensitivity = () =>
  useMutation({
    mutationFn: (request: CombinedAdjustedSensitivityRequest) =>
      calculateCombinedAdjustedSensitivity(request),
    onSuccess: (data) => {
      console.log('[calculateCombinedAdjusted] result:', data);
    },
  });

/**
 * Mutation hook for EV/EBITDA speedometer sensitivity.
 * Returns ev_ebitda_change_percent per year for the gauge display.
 */
export const useSpeedometerSensitivity = () =>
  useMutation({
    mutationFn: (request: SpeedometerSensitivityRequest) =>
      calculateSpeedometerSensitivity(request),
  });
